import { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { careerService } from '../services/api';

const JOB_TYPES = [
    { id: 'general', label: 'General', emoji: '💼' },
    { id: 'internship', label: 'Internship', emoji: '🎓' },
    { id: 'full-time', label: 'Full-time', emoji: '🏢' },
    { id: 'remote', label: 'Remote', emoji: '🌍' },
    { id: 'part-time', label: 'Part-time', emoji: '🕐' },
    { id: 'graduate', label: 'Graduate Program', emoji: '🔬' },
];

const COUNTRIES = [
    'Worldwide',
    'United States', 'United Kingdom', 'Canada', 'Australia',
    'France', 'Germany', 'Netherlands', 'Switzerland', 'Sweden',
    'Ghana', 'Nigeria', 'South Africa', 'Kenya', 'Cameroon',
    'India', 'Singapore', 'UAE', 'Saudi Arabia', 'Qatar',
    'Brazil', 'Mexico', 'Argentina',
    'Japan', 'South Korea',
    'New Zealand', 'Ireland', 'Belgium', 'Spain', 'Italy',
    'Portugal', 'Poland', 'Denmark', 'Norway', 'Finland',
];

function JobsPage() {
    const [aiData, setAiData] = useState(null);
    const [liveJobs, setLiveJobs] = useState([]);
    const [adzunaSupported, setAdzunaSupported] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [selectedType, setSelectedType] = useState('general');
    const [selectedCountry, setSelectedCountry] = useState('Worldwide');

    const fetchJobs = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await careerService.getJobRecommendations(
                selectedType,
                selectedCountry.toLowerCase()
            );
            setAiData(response.data.job_recommendations);
            setLiveJobs(response.data.live_jobs || []);
            setAdzunaSupported(response.data.adzuna_supported);
            setLastUpdated(new Date());
        } catch (err) {
            setError('Could not load job recommendations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatSalary = (min, max) => {
        if (!min && !max) return null;
        const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
        if (min && max) return `${fmt(min)} – ${fmt(max)}`;
        if (min) return `From ${fmt(min)}`;
        if (max) return `Up to ${fmt(max)}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const days = Math.floor((new Date() - date) / (1000 * 60 * 60 * 24));
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
        return `${Math.floor(days / 30)} months ago`;
    };

    const PlatformsCard = () => (
        <div className="card">
            <h2 style={{ marginBottom: '0.25rem' }}>🌐 Search on These Job Boards</h2>
            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                Click any platform to find {selectedType} jobs in {selectedCountry} directly
            </p>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1rem'
            }}>
                {aiData.platforms?.map((platform, i) => (
                    <a
                        key={i}
                        href={platform.url}
                        target="_blank"
                        rel="noreferrer"
                        className="job-platform-card"
                    >
                        <strong>🔗 {platform.name}</strong>
                        <span>{platform.best_for}</span>
                        <span className="job-platform-cta">Search Jobs →</span>
                    </a>
                ))}
            </div>
        </div>
    );

    return (
        <Layout>
            <SEO
                title="Job Search"
                description="Search live job listings across 30+ countries. Find internships, full-time, remote, and graduate roles matched to your profile."
                path="/jobs"
            />
            <div className="page-container">

                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1>💼 Job Recommendations</h1>
                        <p className="subtitle">
                            Real live job listings matched to your profile
                        </p>
                    </div>
                    <button
                        onClick={fetchJobs}
                        disabled={loading}
                        className="btn-primary"
                    >
                        {loading ? '⏳ Finding jobs...' : aiData ? '🔄 Refresh Jobs' : '🤖 Find My Jobs'}
                    </button>
                </div>

                {/* Filters Card */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <h2 style={{ marginBottom: '0.25rem' }}>What are you looking for?</h2>
                    <p style={{ color: '#64748b', marginBottom: '1rem' }}>
                        Select a job type and country to get real live listings
                    </p>

                    {/* Job Type */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                        {JOB_TYPES.map(type => (
                            <button
                                key={type.id}
                                onClick={() => setSelectedType(type.id)}
                                style={{
                                    padding: '0.6rem 1.25rem',
                                    borderRadius: '20px',
                                    border: selectedType === type.id ? '2px solid #3b82f6' : '2px solid #e8ecf0',
                                    background: selectedType === type.id ? '#eff6ff' : '#f8fafc',
                                    color: selectedType === type.id ? '#1d4ed8' : '#64748b',
                                    fontWeight: selectedType === type.id ? '700' : '500',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                }}
                            >
                                {type.emoji} {type.label}
                            </button>
                        ))}
                    </div>

                    {/* Country */}
                    <h2 style={{ marginBottom: '0.25rem' }}>📍 Select Country</h2>
                    <p style={{ color: '#64748b', marginBottom: '0.75rem' }}>
                        Get real job listings from that country
                    </p>
                    <select
                        value={selectedCountry}
                        onChange={e => setSelectedCountry(e.target.value)}
                        style={{
                            width: '100%',
                            maxWidth: '320px',
                            padding: '0.6rem 0.9rem',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            fontSize: '0.95rem',
                            background: 'white',
                            cursor: 'pointer',
                        }}
                    >
                        {COUNTRIES.map(country => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                </div>

                {lastUpdated && (
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '1rem' }}>
                        Last refreshed at {formatTime(lastUpdated)} — click Refresh for latest listings
                    </p>
                )}

                {error && <div className="error-message">{error}</div>}

                {/* Empty state */}
                {!aiData && !loading && (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💼</div>
                        <h2 style={{ marginBottom: '0.5rem' }}>Find Real Jobs Matched to You</h2>
                        <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                            Select a job type and country above, then click Find My Jobs.
                            We search real job boards and filter results based on your profile.
                        </p>
                        <button onClick={fetchJobs} className="btn-primary">
                            🤖 Find My Jobs
                        </button>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <div className="loading-spinner" style={{ margin: '0 auto 1rem' }} />
                        <p style={{ color: '#64748b' }}>
                            Searching {selectedType} jobs in {selectedCountry}...
                        </p>
                    </div>
                )}

                {aiData && !loading && (
                    <>
                        {/* Summary */}
                        <div className="card" style={{
                            background: 'linear-gradient(135deg, #1e293b, #1d4ed8)',
                            color: 'white',
                            marginBottom: '1.5rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ fontSize: '2.5rem' }}>🎯</div>
                                <div>
                                    <h2 style={{ color: 'white', marginBottom: '0.25rem' }}>
                                        {JOB_TYPES.find(t => t.id === selectedType)?.emoji}{' '}
                                        {JOB_TYPES.find(t => t.id === selectedType)?.label} Jobs in {selectedCountry}
                                    </h2>
                                    <p style={{ color: '#94a3b8', margin: 0 }}>
                                        {aiData.summary}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Live Job Listings — only when Adzuna returns results */}
                        {liveJobs.length > 0 && (
                            <div className="card">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                    <h2>🔴 Live Job Listings in {selectedCountry}</h2>
                                    <span style={{
                                        background: '#dcfce7',
                                        color: '#15803d',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        padding: '0.2rem 0.75rem',
                                    }}>
                                        {liveJobs.length} real jobs found
                                    </span>
                                </div>
                                <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                                    Real listings from job boards — click to apply directly
                                </p>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {liveJobs.map((job, i) => (
                                        <div key={i} style={{
                                            background: '#f8fafc',
                                            border: '1px solid #e8ecf0',
                                            borderRadius: '12px',
                                            padding: '1.25rem',
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{ display: 'block', color: '#1e293b', fontSize: '1rem', marginBottom: '0.25rem' }}>
                                                        {job.title}
                                                    </strong>
                                                    <span style={{ color: '#3b82f6', fontSize: '0.875rem', fontWeight: '600' }}>
                                                        🏢 {job.company}
                                                    </span>
                                                    <span style={{ color: '#64748b', fontSize: '0.8rem', marginLeft: '1rem' }}>
                                                        📍 {job.location}
                                                    </span>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
                                                    {formatSalary(job.salary_min, job.salary_max) && (
                                                        <span style={{
                                                            background: '#dcfce7',
                                                            color: '#15803d',
                                                            borderRadius: '20px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: '600',
                                                            padding: '0.2rem 0.6rem',
                                                        }}>
                                                            💰 {formatSalary(job.salary_min, job.salary_max)}
                                                        </span>
                                                    )}
                                                    {job.posted_date && (
                                                        <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                                                            🕐 {formatDate(job.posted_date)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {job.description && (
                                                <p style={{
                                                    color: '#475569',
                                                    fontSize: '0.85rem',
                                                    lineHeight: '1.5',
                                                    margin: '0.75rem 0',
                                                }}>
                                                    {job.description}
                                                </p>
                                            )}

                                            <a
                                                href={job.apply_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    display: 'inline-block',
                                                    background: '#3b82f6',
                                                    color: 'white',
                                                    padding: '0.5rem 1.25rem',
                                                    borderRadius: '8px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    marginTop: '0.5rem',
                                                }}
                                            >
                                                Apply Now →
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Warning when Adzuna supported but no results */}
                        {liveJobs.length === 0 && adzunaSupported && (
                            <div className="card" style={{ background: '#fefce8', border: '1px solid #fde68a' }}>
                                <p style={{ color: '#b45309', fontSize: '0.9rem', margin: 0 }}>
                                    ⚠️ No live listings found right now — search manually on the platforms below.
                                </p>
                            </div>
                        )}

                        {/* Job Platforms — always show exactly once */}
                        <PlatformsCard />

                        {/* AI Recommended Job Titles */}
                        <div className="card">
                            <h2 style={{ marginBottom: '0.25rem' }}>🤖 AI Recommended Job Titles</h2>
                            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                                Search for these roles on the job boards above in {selectedCountry}
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {aiData.job_titles?.map((job, i) => (
                                    <div key={i} style={{
                                        background: '#f8fafc',
                                        border: '1px solid #e8ecf0',
                                        borderRadius: '12px',
                                        padding: '1rem 1.25rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '1rem',
                                        flexWrap: 'wrap',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                                            <div style={{
                                                background: '#1e293b',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '700',
                                                fontSize: '0.85rem',
                                                flexShrink: 0,
                                            }}>
                                                {i + 1}
                                            </div>
                                            <div>
                                                <strong style={{ display: 'block', color: '#1e293b', fontSize: '0.95rem' }}>
                                                    {job.title}
                                                </strong>
                                                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                    {job.reason}
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <span style={{
                                                background: '#dbeafe', color: '#1d4ed8',
                                                borderRadius: '20px', fontSize: '0.75rem',
                                                fontWeight: '600', padding: '0.2rem 0.6rem',
                                            }}>
                                                {job.industry}
                                            </span>
                                            <span style={{
                                                background: job.level === 'Entry Level' ? '#dcfce7' : '#fef3c7',
                                                color: job.level === 'Entry Level' ? '#15803d' : '#b45309',
                                                borderRadius: '20px', fontSize: '0.75rem',
                                                fontWeight: '600', padding: '0.2rem 0.6rem',
                                            }}>
                                                {job.level}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Search Tips */}
                        <div className="card" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <h2 style={{ marginBottom: '1rem', color: '#15803d' }}>
                                💡 Job Search Tips for {selectedCountry}
                            </h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {aiData.search_tips?.map((tip, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                                        <div style={{
                                            background: '#15803d', color: 'white',
                                            borderRadius: '50%', width: '24px', height: '24px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.75rem', fontWeight: '700',
                                            flexShrink: 0, marginTop: '0.1rem'
                                        }}>
                                            {i + 1}
                                        </div>
                                        <p style={{ color: '#374151', fontSize: '0.9rem', margin: 0, lineHeight: '1.5' }}>
                                            {tip}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

export default JobsPage;